
import type React from "react"

import { toast } from "sonner"
import { useState } from "react"
import { useChangePassword } from "@/hooks"
import { StyledInput } from "@/components/ui/styled-input"
import { StyledLabel } from "@/components/ui/styled-label"
import { useTranslation } from 'react-i18next'
import { Button } from "@/components/ui/button"
import { Loader2, Eye, EyeOff } from "lucide-react"
import { useAuthStore } from "@/store/use-auth-store"

const PasswordForm = () => {
    const { t } = useTranslation();
    const { user } = useAuthStore()
    const [currentPassword, setCurrentPassword] = useState("")
    const [newPassword, setNewPassword] = useState("")
    const [confirmPassword, setConfirmPassword] = useState("")
    const [isUpdating, setIsUpdating] = useState(false)
    const [showCurrentPassword, setShowCurrentPassword] = useState(false)
    const [showNewPassword, setShowNewPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)

    const changePasswordMutation = useChangePassword()

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        // Validate passwords
        if (newPassword !== confirmPassword) {
            toast.error(t('profile.newPasswordsDoNotMatch'))
            return
        }

        // Validate password strength
        if (newPassword.length < 8) {
            toast.error(t('profile.passwordTooShort'))
            return
        }

        if (!/[a-z]/.test(newPassword)) {
            toast.error(t('profile.passwordNeedsLowercase'))
            return
        }

        if (!/[A-Z]/.test(newPassword)) {
            toast.error(t('profile.passwordNeedsUppercase'))
            return
        }

        if (!/[0-9]/.test(newPassword)) {
            toast.error(t('profile.passwordNeedsNumber'))
            return
        }

        if (!/[!@#$%^&*(),.?":{}|<>]/.test(newPassword)) {
            toast.error(t('profile.passwordNeedsSpecialChar'))
            return
        }

        try {
            setIsUpdating(true)

            const response = await changePasswordMutation.mutateAsync({
                userId: user?.id || '',
                currentPassword,
                newPassword,
            })

            if (!response) {
                throw new Error(t('profile.failedToChangePassword'));
            }

            // Reset form
            setCurrentPassword("")
            setNewPassword("")
            setConfirmPassword("")

            toast.success(t('profile.passwordChangedSuccessfully'))
        } catch (error: any) {
            console.error("Password change error", error)
            toast.error(t('profile.failedToChangePassword'))
        } finally {
            setIsUpdating(false)
        }
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
                <div>
                    <StyledLabel htmlFor="currentPassword">{t('profile.currentPassword')}</StyledLabel>
                    <StyledInput
                        id="currentPassword"
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        placeholder={t('profile.enterCurrentPassword')}
                        required
                    />
                </div>

                <div>
                    <StyledLabel htmlFor="newPassword">{t('profile.newPassword')}</StyledLabel>
                    <StyledInput
                        id="newPassword"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder={t('profile.enterNewPassword')}
                        required
                        minLength={8}
                    />
                </div>

                <div>
                    <StyledLabel htmlFor="confirmPassword">{t('profile.confirmNewPassword')}</StyledLabel>
                    <StyledInput
                        id="confirmPassword"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder={t('profile.confirmNewPasswordPlaceholder')}
                        required
                    />
                </div>
            </div>

            <div className="flex justify-end">
                <Button type="submit" className="main-dark-button" disabled={isUpdating}>
                    {isUpdating ? (
                        <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            {t('profile.updating')}
                        </>
                    ) : (
                        t('profile.changePassword')
                    )}
                </Button>
            </div>
        </form>
    )
}

export default PasswordForm